/**
 * Pure clip/format helpers extracted verbatim from src/components/studio/VideoEditor.tsx
 * (editor V2 modular rewrite, task 1). No React, no store — pure functions only.
 */
import {
  persistableEditorAssets,
  persistableEditorClips,
  persistableEditorLayers,
  recalcEditorClipStarts,
} from '@/lib/editor/render-contract';
import type {
  EditorAspectRatio,
  EditorOverlayLayer as OverlayLayer,
  EditorStickerAsset as StickerAsset,
  EditorTimelineClip as TimelineClip,
} from '@/types/editor';

/**
 * Minimal media shape consumed by makeClip. Mirrors the local `EditorMedia`
 * type defined in VideoEditor.tsx (not exported from @/types/editor).
 */
export type EditorMedia = {
  id: string;
  videoId?: string;
  title: string;
  src: string;
  source: 'upload' | 'studio';
  createdAt?: string;
  duration?: number;
  contentAnalysis?: {
    status: 'pending' | 'processing' | 'ready' | 'failed';
    summary?: string;
    tags?: string[];
    transcript?: string;
  };
  file?: File;
};

export function persistableClips(clips: TimelineClip[]) {
  return persistableEditorClips(clips);
}

export function persistableAssets(assets: StickerAsset[]) {
  return persistableEditorAssets(assets);
}

export function persistableLayers(layers: OverlayLayer[]) {
  return persistableEditorLayers(layers);
}

export function makeId(prefix: string) {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function safeClipDuration(value?: number) {
  return Number.isFinite(value) && value && value > 0 ? value : 6;
}

export const ASPECT_CLASS: Record<EditorAspectRatio, string> = {
  '9:16': 'aspect-[9/16]',
  '16:9': 'aspect-[16/9]',
  '1:1': 'aspect-square',
};

export const ASPECT_TARGETS: Array<[EditorAspectRatio, number]> = [
  ['9:16', 9 / 16],
  ['16:9', 16 / 9],
  ['1:1', 1],
];

/** Fit an aspect-ratio rectangle inside the available player viewport. */
export function fitAspectRatioInBounds(
  boundsWidth: number,
  boundsHeight: number,
  aspectRatio: EditorAspectRatio
): { width: number; height: number } {
  const width = Math.max(0, Number.isFinite(boundsWidth) ? boundsWidth : 0);
  const height = Math.max(0, Number.isFinite(boundsHeight) ? boundsHeight : 0);
  if (width === 0 || height === 0) return { width: 0, height: 0 };

  const ratio = ASPECT_TARGETS.find(([key]) => key === aspectRatio)?.[1] ?? 9 / 16;
  const fittedWidth = Math.min(width, height * ratio);
  return { width: fittedWidth, height: fittedWidth / ratio };
}

export function nearestAspectRatio(ratio: number): EditorAspectRatio {
  let nearest: EditorAspectRatio = '9:16';
  let smallestDiff = Infinity;
  for (const [key, value] of ASPECT_TARGETS) {
    const diff = Math.abs(ratio - value);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      nearest = key;
    }
  }
  return nearest;
}

export function detectVideoAspectRatio(src: string): Promise<EditorAspectRatio | null> {
  return new Promise((resolve) => {
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      const cleanup = () => {
        video.onloadedmetadata = null;
        video.onerror = null;
        try {
          video.src = '';
          video.removeAttribute('src');
        } catch {
          // ignore
        }
      };
      video.onloadedmetadata = () => {
        const { videoWidth, videoHeight } = video;
        cleanup();
        if (!videoWidth || !videoHeight) {
          resolve(null);
          return;
        }
        resolve(nearestAspectRatio(videoWidth / videoHeight));
      };
      video.onerror = () => {
        cleanup();
        resolve(null);
      };
      video.src = src;
    } catch {
      resolve(null);
    }
  });
}

export function makeClip(media: EditorMedia, start: number): TimelineClip {
  return {
    id: makeId('clip'),
    mediaId: media.id,
    videoId: media.videoId,
    title: media.title,
    src: media.src,
    source: media.source,
    createdAt: media.createdAt,
    start,
    duration: safeClipDuration(media.duration),
    mediaOffset: 0,
    volume: 1,
  };
}

export function recalcClipStarts(clips: TimelineClip[]) {
  let cursor = 0;
  return clips.map((clip) => {
    const next = { ...clip, start: cursor };
    cursor += safeClipDuration(clip.duration);
    return next;
  });
}

export function splitClipAt(
  clips: TimelineClip[],
  clipId: string,
  atGlobalSeconds: number,
  makeIdFn: (prefix: string) => string
): TimelineClip[] {
  const idx = clips.findIndex((c) => c.id === clipId);
  if (idx < 0) return clips;
  const c = clips[idx];
  const offsetWithin = atGlobalSeconds - c.start;
  if (offsetWithin <= 0.5 || offsetWithin >= c.duration - 0.5) return clips;
  const left: TimelineClip = { ...c, duration: offsetWithin };
  const right: TimelineClip = {
    ...c,
    id: makeIdFn('clip'),
    mediaOffset: c.mediaOffset + offsetWithin,
    duration: c.duration - offsetWithin,
    isTrimmed: true,
    // The split point is a hard cut from `left`, so `right`'s incoming edge
    // has no transition — only `left` keeps the original transitionIn.
    transitionIn: undefined,
  };
  return recalcEditorClipStarts([...clips.slice(0, idx), left, right, ...clips.slice(idx + 1)]);
}
